import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { breakpoints, colors, fontSizes, fontWeights } from '@/styles'
import { cardTypes } from '@/features/Payments/forms/credit-card-input'
import { formatExpiry } from '@/utils/payments'
import Skeleton from '@/components/skeleton'
import { PaymentMethod } from './use-payment-methods'

const CardInfoWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  flex: 1;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    flex-direction: column;
  }
`

const CardImage = styled.img`
  width: 48px;
  margin-right: 24px;
`

const CardInfo = styled.div`
  display: flex;
  flex-direction: column;

  p:not(:last-child) {
    margin-bottom: 4px;
  }

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    margin: 16px 0 24px;
  }
`

const CardholderName = styled.p`
  font-size: ${fontSizes.regular};
  line-height: 20px;
  font-weight: ${fontWeights.bold};
  margin: 0;
`

const CardText = styled.p`
  font-size: 14px;
  line-height: 20px;
  color: ${colors.mediumGrey};
  margin: 0;
`

type CardSummaryProps = {
  card?: PaymentMethod
}

export const CardSummary: React.FC<CardSummaryProps> = ({ card }) => {
  const { t } = useTranslation()
  const cardType = card?.brand ?? 'unknown'
  const lastFourDigits = card?.card_last4
  const cardName = cardTypes[cardType].name

  return (
    <CardText>
      <Trans
        i18nKey="l.plan_settings.view.card_info_digits.text"
        values={{ cardName, lastFourDigits }}
        defaults="{{ cardName }} ending in {{ lastFourDigits }}"
        t={t}
      />
    </CardText>
  )
}

type PaymentSummaryProps = {
  card?: PaymentMethod
  showType?: boolean
  isLoading?: boolean
}

const PaymentSummary: React.FC<PaymentSummaryProps> = ({
  card,
  showType = false,
  isLoading = false
}) => {
  const cardType = card?.brand ?? 'unknown'
  const expirationDate = formatExpiry(card?.expiration_date)
  const cardLogo = cardTypes[cardType].flag

  if (isLoading)
    return (
      <CardInfoWrapper>
        {showType && <Skeleton height={30} width={48} right={24} />}
        <CardInfo>
          <Skeleton height={20} width={180} bottom={6} />
          <Skeleton height={16} width={150} bottom={6} />
          <Skeleton height={16} width={120} />
        </CardInfo>
      </CardInfoWrapper>
    )

  return (
    <CardInfoWrapper>
      {showType && <CardImage src={cardLogo} />}
      <CardInfo>
        <CardholderName>
          {card?.first_name} {card?.last_name}
        </CardholderName>
        <CardSummary card={card} />
        <CardText>
          <Trans
            i18nKey="l.plan_settings.view.card_info_expiration.text"
            values={{ expirationDate }}
            defaults="Expires {{ expirationDate }}"
          />
        </CardText>
      </CardInfo>
    </CardInfoWrapper>
  )
}

export default PaymentSummary
